import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { fetchGet, fetchPut, fetchDelete } from '../../../../../helpers/fetching';
import { setSnackbar } from '../../../../../store';

const useStyles = makeStyles((theme) => ({
  titleSettingsCard: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontSize: '1.1rem',
  },
  listContainer: {
    marginTop: 16,
    padding: 16,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  listTitle: {
    fontSize: theme.fonts.responsiveMedium,
    letterSpacing: '0.1rem',
    color: theme.palette.texts.primary,
    fontWeight: 'bold',
  },
  selectorControl: {
    minWidth: 280,
  },
}));

export default function OptingOutOperators() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [operators, setOperators] = useState([]);
  const [optedOutOperators, setOptedOutOperators] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const fetchOperators = useCallback(async () => {
    const response = await fetchGet('operators');
    if (response.success) {
      setOperators(response.data || []);
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
    }
  }, [dispatch]);

  const fetchOptedOutOperators = useCallback(async () => {
    setLoading(true);
    const response = await fetchGet('operators_opted_out');
    if (response.success) {
      setOptedOutOperators(response.data || []);
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    fetchOperators();
    fetchOptedOutOperators();
  }, [fetchOperators, fetchOptedOutOperators]);

  const availableOperators = useMemo(() => {
    const optedOutIds = new Set(optedOutOperators.map((op) => op.operator_id));
    return operators.filter((operator) => !optedOutIds.has(operator.id));
  }, [operators, optedOutOperators]);

  async function addOperator() {
    const operator_id = Number.parseInt(selectedOperatorId, 10);
    if (!operator_id) return;

    setAdding(true);
    const response = await fetchPut('operators_opted_out', { operator_id });
    if (response.success) {
      setSelectedOperatorId('');
      await fetchOptedOutOperators();
      dispatch(
        setSnackbar({
          msg: 'Settings.operatorsOptOutAdded',
          severity: 'success',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
    }
    setAdding(false);
  }

  async function removeOperator(operator_id) {
    const response = await fetchDelete('operators_opted_out', { operator_id });
    if (response.success) {
      setOptedOutOperators((prev) => prev.filter((operator) => operator.operator_id !== operator_id));
      dispatch(
        setSnackbar({
          msg: 'Settings.operatorsOptOutRemoved',
          severity: 'success',
        })
      );
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
    }
  }

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item xs={12}>
        <Card elevation={1}>
          <CardHeader
            disableTypography
            title={
              <Typography className={classes.titleSettingsCard} variant='h6'>
                {t('Settings.optingOutOperators')}
              </Typography>
            }
          />
          <CardContent style={{ paddingTop: 0 }}>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs>
                <FormControl fullWidth className={classes.selectorControl}>
                  <InputLabel>{t('Settings.operator')}</InputLabel>
                  <Select
                    value={selectedOperatorId}
                    onChange={(e) => setSelectedOperatorId(e.target.value)}
                    label={t('Settings.operator')}>
                    {availableOperators.map((operator) => (
                      <MenuItem key={operator.id} value={operator.id}>
                        {operator.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={addOperator}
                  disabled={adding || !selectedOperatorId}>
                  {adding ? <CircularProgress size={24} /> : t('Settings.addOperator')}
                </Button>
              </Grid>
            </Grid>

            {loading ? (
              <CircularProgress style={{ marginTop: 16 }} />
            ) : optedOutOperators.length === 0 ? (
              <Typography color='textSecondary' style={{ marginTop: 16 }}>
                {t('Settings.operatorsOptOutEmpty')}
              </Typography>
            ) : (
              <div className={classes.listContainer}>
                <Typography className={classes.listTitle} variant='h6'>
                  {t('Settings.optingOutOperators')}
                </Typography>
                <List dense>
                  {optedOutOperators.map((operator) => (
                    <ListItem key={operator.operator_id} divider>
                      <ListItemText primary={operator.name} secondary={operator.institution || null} />
                      <IconButton
                        edge='end'
                        aria-label='delete'
                        onClick={() => removeOperator(operator.operator_id)}
                        size='small'>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </div>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
